import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { 
  Save, History, Variable, Clock, 
  Play, FlaskConical, FileText, Eye,
  Hash, Copy, Bold, Italic,
  List, ListOrdered, Code, Heading1, Heading2,
  Quote, Minus, ArrowRight, CornerDownRight,
  Braces, MessageSquare, Lightbulb, Target,
  Layers, CheckSquare, AlertCircle, Wand2,
  IndentIncrease, IndentDecrease, Maximize2,
  ChevronDown, ChevronUp, Loader2, Check, X,
  Beaker
} from 'lucide-react';
import { updatePrompt, getPromptVersions, restoreVersion, getModels, countTokens, getTestCases, runTestCase } from '../../lib/api';
import VersionHistory from './VersionHistory';
import ModelSelector from './ModelSelector';
import TestCasePanel from './TestCasePanel';
import PromptPreview from './PromptPreview';

// Prompt templates for best practices
const PROMPT_TEMPLATES = {
  system_role: `You are a [ROLE]. Your expertise includes [EXPERTISE].

## Your Responsibilities:
- [RESPONSIBILITY_1]
- [RESPONSIBILITY_2]
- [RESPONSIBILITY_3]

## Guidelines:
1. Always [GUIDELINE_1]
2. Never [GUIDELINE_2]
3. When uncertain, [GUIDELINE_3]`,

  chain_of_thought: `Let's approach this step-by-step:

## Step 1: Understanding the Problem
First, I need to understand what {input} is asking for.

## Step 2: Breaking Down the Components
- Component A: [ANALYSIS]
- Component B: [ANALYSIS]
- Component C: [ANALYSIS]

## Step 3: Reasoning Through Each Part
For each component, I will consider:
1. What are the key factors?
2. What are the potential outcomes?
3. What is the best approach?

## Step 4: Synthesizing the Solution
Based on my analysis, the solution is...

## Step 5: Verification
Let me verify this solution by checking...`,

  few_shot: `I'll show you some examples first, then you'll complete a similar task.

## Example 1:
**Input:** [EXAMPLE_INPUT_1]
**Output:** [EXAMPLE_OUTPUT_1]

## Example 2:
**Input:** [EXAMPLE_INPUT_2]
**Output:** [EXAMPLE_OUTPUT_2]

## Example 3:
**Input:** [EXAMPLE_INPUT_3]
**Output:** [EXAMPLE_OUTPUT_3]

---

Now, please complete the following:

**Input:** {input}
**Output:**`,

  structured_output: `Analyze the following and provide your response in the exact format specified below.

## Input:
{input}

## Required Output Format:
\`\`\`json
{
  "summary": "Brief summary in 1-2 sentences",
  "key_points": ["point1", "point2", "point3"],
  "sentiment": "positive|negative|neutral",
  "confidence": 0.0-1.0,
  "recommendations": ["action1", "action2"]
}
\`\`\`

## Instructions:
1. Ensure all fields are populated
2. Keep summary concise
3. Provide exactly 3 key points
4. Base confidence on available evidence`,

  critique_refine: `## Task: {task}

## Initial Response:
[Generate initial response here]

## Self-Critique:
- What are the strengths of this response?
- What are the weaknesses?
- What information might be missing?
- Are there any logical errors?

## Refined Response:
[Based on the critique, provide an improved response]

## Verification:
- Does the refined response address all weaknesses?
- Is it accurate and complete?
- Does it meet the original requirements?`
};

export default function PromptEditor({ prompt, taskName, onUpdate, onImportComplete }) {
  const textareaRef = useRef(null);
  const [content, setContent] = useState('');
  const [primaryModels, setPrimaryModels] = useState([]);
  const [secondaryModel, setSecondaryModel] = useState(null);
  const [allowMultiPrimary, setAllowMultiPrimary] = useState(false);
  const [versions, setVersions] = useState([]);
  const [availableModels, setAvailableModels] = useState({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [tokenCount, setTokenCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Quick Test states
  const [showQuickTest, setShowQuickTest] = useState(false);
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [quickTestModel, setQuickTestModel] = useState({ provider: 'openai', model: 'gpt-4o' });
  const [runningTests, setRunningTests] = useState({});
  const [testResults, setTestResults] = useState({});

  // Extract variables from content
  const variables = useMemo(() => {
    if (!content) return [];
    const pattern = /\{(\w+)\}/g;
    const matches = [...content.matchAll(pattern)];
    return [...new Set(matches.map(m => m[1]))];
  }, [content]);

  // Count tokens with debounce
  const updateTokenCount = useCallback(async (text) => {
    if (!text) {
      setTokenCount(0);
      return;
    }
    try {
      const res = await countTokens(text);
      setTokenCount(res.data.tokens);
    } catch (err) {
      setTokenCount(Math.ceil(text.length / 4));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateTokenCount(content);
    }, 500);
    return () => clearTimeout(timer);
  }, [content, updateTokenCount]);

  // Load available models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await getModels();
        setAvailableModels(res.data);
      } catch (err) {
        console.error('Failed to load models:', err);
      }
    };
    loadModels();
  }, []);

  // Load prompt data
  useEffect(() => {
    if (prompt) {
      setContent(prompt.content);
      setPrimaryModels(prompt.primary_models || []);
      setSecondaryModel(prompt.secondary_model);
      setAllowMultiPrimary(prompt.allow_multi_primary || false);
      setHasChanges(false);
      setChangeNote('');
      loadVersions(prompt.id);
      loadTestCases(prompt.id);
    } else {
      setContent('');
      setPrimaryModels([]);
      setSecondaryModel(null);
      setAllowMultiPrimary(false);
      setVersions([]);
      setTestCases([]);
      setSelectedTestCases([]);
      setTestResults({});
      setHasChanges(false);
    }
  }, [prompt]);

  const loadVersions = async (promptId) => {
    try {
      const res = await getPromptVersions(promptId);
      setVersions(res.data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const loadTestCases = async (promptId) => {
    try {
      const res = await getTestCases(promptId);
      setTestCases(res.data);
    } catch (err) {
      console.error('Failed to load test cases:', err);
    }
  };

  // Quick Test handlers
  const toggleTestCaseSelection = (testCaseId) => {
    setSelectedTestCases(prev => 
      prev.includes(testCaseId) 
        ? prev.filter(id => id !== testCaseId)
        : [...prev, testCaseId]
    );
  };

  const selectAllTestCases = () => {
    if (selectedTestCases.length === testCases.length) {
      setSelectedTestCases([]);
    } else {
      setSelectedTestCases(testCases.map(tc => tc.id));
    }
  };

  const runSelectedTests = async () => {
    if (selectedTestCases.length === 0) {
      toast.error('Please select at least one test case');
      return;
    }

    // Run all selected tests
    for (const testCaseId of selectedTestCases) {
      setRunningTests(prev => ({ ...prev, [testCaseId]: true }));
      try {
        // Pass current content to run against unsaved prompt
        const res = await runTestCase(
          testCaseId, 
          quickTestModel.provider, 
          quickTestModel.model,
          hasChanges ? content : null
        );
        setTestResults(prev => ({ ...prev, [testCaseId]: res.data }));
        if (res.data.success && res.data.all_passed) {
          toast.success(`Test "${testCases.find(tc => tc.id === testCaseId)?.name}" passed`);
        } else if (res.data.success) {
          toast.warning(`Test "${testCases.find(tc => tc.id === testCaseId)?.name}" - some assertions failed`);
        } else {
          toast.error(`Test "${testCases.find(tc => tc.id === testCaseId)?.name}" failed`);
        }
      } catch (err) {
        toast.error(`Failed to run test: ${err.message}`);
        setTestResults(prev => ({ ...prev, [testCaseId]: { success: false, error: err.message } }));
      } finally {
        setRunningTests(prev => ({ ...prev, [testCaseId]: false }));
      }
    }
  };

  const runSingleTest = async (testCaseId) => {
    setRunningTests(prev => ({ ...prev, [testCaseId]: true }));
    try {
      const res = await runTestCase(
        testCaseId,
        quickTestModel.provider,
        quickTestModel.model,
        hasChanges ? content : null
      );
      setTestResults(prev => ({ ...prev, [testCaseId]: res.data }));
      if (res.data.success && res.data.all_passed) {
        toast.success('Test passed!');
      } else if (res.data.success) {
        toast.warning('Some assertions failed');
      } else {
        toast.error(res.data.error || 'Test failed');
      }
    } catch (err) {
      toast.error(`Failed to run test: ${err.message}`);
    } finally {
      setRunningTests(prev => ({ ...prev, [testCaseId]: false }));
    }
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setHasChanges(true);
  };

  // Get selection info
  const getSelection = () => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0, text: '' };
    return {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
      text: content.substring(textarea.selectionStart, textarea.selectionEnd)
    };
  };

  // Insert text at cursor or wrap selection
  const insertText = (before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const { start, end, text } = getSelection();
    const selectedText = text || placeholder;
    const newContent = content.substring(0, start) + before + selectedText + after + content.substring(end);
    
    setContent(newContent);
    setHasChanges(true);
    
    // Set cursor position
    setTimeout(() => {
      const newPos = start + before.length + (text ? text.length : 0);
      textarea.focus();
      textarea.setSelectionRange(
        text ? start + before.length : newPos,
        text ? start + before.length + text.length : newPos + placeholder.length
      );
    }, 0);
  };

  // Insert at beginning of selected lines
  const insertAtLineStart = (prefix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const { start, end } = getSelection();
    const lines = content.split('\n');
    let charCount = 0;
    let startLine = 0;
    let endLine = 0;
    
    for (let i = 0; i < lines.length; i++) {
      if (charCount <= start) startLine = i;
      if (charCount <= end) endLine = i;
      charCount += lines[i].length + 1;
    }
    
    const newLines = lines.map((line, i) => {
      if (i >= startLine && i <= endLine) {
        return prefix + line;
      }
      return line;
    });
    
    setContent(newLines.join('\n'));
    setHasChanges(true);
  };

  // Formatting functions
  const formatBold = () => insertText('**', '**', 'bold text');
  const formatItalic = () => insertText('*', '*', 'italic text');
  const formatCode = () => insertText('`', '`', 'code');
  const formatCodeBlock = () => insertText('\n```\n', '\n```\n', 'code block');
  const formatH1 = () => insertText('\n# ', '\n', 'Heading 1');
  const formatH2 = () => insertText('\n## ', '\n', 'Heading 2');
  const formatQuote = () => insertAtLineStart('> ');
  const formatBullet = () => insertAtLineStart('- ');
  const formatNumbered = () => insertAtLineStart('1. ');
  const formatIndent = () => insertAtLineStart('  ');
  const formatOutdent = () => {
    const { start, end } = getSelection();
    const lines = content.split('\n');
    let charCount = 0;
    let startLine = 0;
    let endLine = 0;
    
    for (let i = 0; i < lines.length; i++) {
      if (charCount <= start) startLine = i;
      if (charCount <= end) endLine = i;
      charCount += lines[i].length + 1;
    }
    
    const newLines = lines.map((line, i) => {
      if (i >= startLine && i <= endLine && line.startsWith('  ')) {
        return line.substring(2);
      }
      return line;
    });
    
    setContent(newLines.join('\n'));
    setHasChanges(true);
  };
  const formatSeparator = () => insertText('\n---\n', '', '');
  
  // Insert variable
  const insertVariable = (varName) => {
    insertText(`{${varName}}`, '', '');
  };

  // Insert prompt template
  const insertTemplate = (templateKey) => {
    const template = PROMPT_TEMPLATES[templateKey];
    if (template) {
      const { start } = getSelection();
      const newContent = content.substring(0, start) + template + content.substring(start);
      setContent(newContent);
      setHasChanges(true);
      toast.success('Template inserted');
    }
  };

  // Copy prompt
  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Prompt copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleSave = async () => {
    if (!prompt) return;
    setSaving(true);
    try {
      const res = await updatePrompt(prompt.id, {
        content,
        primary_models: primaryModels,
        secondary_model: secondaryModel,
        allow_multi_primary: allowMultiPrimary,
        change_note: changeNote || `Updated prompt content`
      });
      onUpdate(res.data);
      setHasChanges(false);
      setChangeNote('');
      await loadVersions(prompt.id);
      toast.success('Prompt saved successfully');
    } catch (err) {
      toast.error('Failed to save prompt');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (versionNum) => {
    if (!prompt) return;
    try {
      const res = await restoreVersion(prompt.id, versionNum);
      onUpdate(res.data);
      setShowHistory(false);
      toast.success(`Restored to version ${versionNum}`);
    } catch (err) {
      toast.error('Failed to restore version');
      console.error(err);
    }
  };

  const handleVersionDeleted = async () => {
    if (prompt) {
      await loadVersions(prompt.id);
    }
  };

  const handlePrimaryModelChange = (model) => {
    if (allowMultiPrimary) {
      const exists = primaryModels.find(
        m => m.provider === model.provider && m.model_name === model.model_name
      );
      if (exists) {
        setPrimaryModels(primaryModels.filter(
          m => !(m.provider === model.provider && m.model_name === model.model_name)
        ));
      } else {
        setPrimaryModels([...primaryModels, model]);
      }
    } else {
      setPrimaryModels([model]);
    }
    setHasChanges(true);
  };

  const handleSecondaryModelChange = (model) => {
    setSecondaryModel(model);
    setHasChanges(true);
  };

  // Syntax highlighted content
  const highlightedContent = useMemo(() => {
    if (!content) return '';
    return content.replace(
      /\{(\w+)\}/g,
      '<span class="text-[#0EA5E9] bg-[#0EA5E9]/10 px-1 rounded">{$1}</span>'
    );
  }, [content]);

  if (!prompt) {
    return (
      <div className="h-full bg-[#0F172A] flex items-center justify-center" data-testid="prompt-editor-empty">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-md bg-[#1E293B] flex items-center justify-center mx-auto mb-4">
            <Variable className="w-8 h-8 text-[#64748B]" />
          </div>
          <p className="text-[#64748B] text-sm">Select a task to view its prompt</p>
        </div>
      </div>
    );
  }

  // Toolbar button component
  const ToolbarButton = ({ icon: Icon, label, onClick, active = false }) => (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={cn(
              "p-1.5 rounded-sm transition-colors",
              active 
                ? "bg-[#0EA5E9]/20 text-[#0EA5E9]" 
                : "text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
            )}
          >
            <Icon className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
          <p className="text-xs">{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className={cn(
      "h-full bg-[#0F172A] flex flex-col",
      isFullscreen && "fixed inset-0 z-50"
    )} data-testid="prompt-editor">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-[#F1F5F9]">{prompt.name}</h2>
            <Badge variant="outline" className="text-[10px] font-mono">
              v{prompt.current_version}
            </Badge>
            {hasChanges && (
              <Badge className="text-[10px] bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30">
                Unsaved
              </Badge>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-0.5">{taskName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={copyPrompt}
            className="text-[#94A3B8] hover:text-white"
            data-testid="copy-prompt-btn"
          >
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="text-[#94A3B8] hover:text-white"
            data-testid="toggle-history-btn"
          >
            <History className="w-4 h-4 mr-1" />
            History
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            data-testid="save-prompt-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Editor */}
        <div className={cn("flex-1 flex flex-col", showHistory && "border-r border-[#1E293B]")}>
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <div className="border-b border-[#1E293B] px-4">
              <TabsList className="bg-transparent h-10">
                <TabsTrigger 
                  value="editor" 
                  className="data-[state=active]:bg-[#1E293B] data-[state=active]:text-white"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Editor
                </TabsTrigger>
                <TabsTrigger 
                  value="preview"
                  className="data-[state=active]:bg-[#1E293B] data-[state=active]:text-white"
                >
                  <Eye className="w-4 h-4 mr-1.5" />
                  Preview
                </TabsTrigger>
                <TabsTrigger 
                  value="test"
                  className="data-[state=active]:bg-[#1E293B] data-[state=active]:text-white"
                >
                  <FlaskConical className="w-4 h-4 mr-1.5" />
                  Test Cases
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="editor" className="flex-1 overflow-hidden m-0 flex flex-col">
              {/* Formatting Toolbar */}
              <div className="px-4 py-2 border-b border-[#1E293B] bg-[#030712]">
                <div className="flex items-center gap-1 flex-wrap">
                  {/* Text Formatting */}
                  <div className="flex items-center gap-0.5 pr-2 border-r border-[#1E293B]">
                    <ToolbarButton icon={Bold} label="Bold (Ctrl+B)" onClick={formatBold} />
                    <ToolbarButton icon={Italic} label="Italic (Ctrl+I)" onClick={formatItalic} />
                    <ToolbarButton icon={Code} label="Inline Code" onClick={formatCode} />
                  </div>
                  
                  {/* Headers */}
                  <div className="flex items-center gap-0.5 px-2 border-r border-[#1E293B]">
                    <ToolbarButton icon={Heading1} label="Heading 1" onClick={formatH1} />
                    <ToolbarButton icon={Heading2} label="Heading 2" onClick={formatH2} />
                  </div>
                  
                  {/* Lists & Structure */}
                  <div className="flex items-center gap-0.5 px-2 border-r border-[#1E293B]">
                    <ToolbarButton icon={List} label="Bullet List" onClick={formatBullet} />
                    <ToolbarButton icon={ListOrdered} label="Numbered List" onClick={formatNumbered} />
                    <ToolbarButton icon={Quote} label="Quote" onClick={formatQuote} />
                    <ToolbarButton icon={Minus} label="Separator" onClick={formatSeparator} />
                  </div>
                  
                  {/* Indentation */}
                  <div className="flex items-center gap-0.5 px-2 border-r border-[#1E293B]">
                    <ToolbarButton icon={IndentIncrease} label="Indent" onClick={formatIndent} />
                    <ToolbarButton icon={IndentDecrease} label="Outdent" onClick={formatOutdent} />
                    <ToolbarButton icon={Braces} label="Code Block" onClick={formatCodeBlock} />
                  </div>

                  {/* Prompt Templates */}
                  <div className="flex items-center gap-0.5 px-2 border-r border-[#1E293B]">
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => insertTemplate('system_role')}
                            className="p-1.5 rounded-sm text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
                          <p className="text-xs">System Role Template</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => insertTemplate('chain_of_thought')}
                            className="p-1.5 rounded-sm text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                          >
                            <Lightbulb className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
                          <p className="text-xs">Chain-of-Thought Template</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => insertTemplate('few_shot')}
                            className="p-1.5 rounded-sm text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                          >
                            <Layers className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
                          <p className="text-xs">Few-Shot Template</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => insertTemplate('structured_output')}
                            className="p-1.5 rounded-sm text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                          >
                            <Target className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
                          <p className="text-xs">Structured Output Template</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => insertTemplate('critique_refine')}
                            className="p-1.5 rounded-sm text-[#64748B] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                          >
                            <CheckSquare className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1E293B] text-[#F1F5F9] border-[#334155]">
                          <p className="text-xs">Critique & Refine Template</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 pl-2">
                    <ToolbarButton icon={Copy} label="Copy Prompt" onClick={copyPrompt} />
                    <ToolbarButton 
                      icon={Maximize2} 
                      label="Fullscreen" 
                      onClick={() => setIsFullscreen(!isFullscreen)} 
                      active={isFullscreen}
                    />
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                  {/* Model Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-[#94A3B8]">Model Configuration</Label>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="multi-primary"
                          checked={allowMultiPrimary}
                          onCheckedChange={(checked) => {
                            setAllowMultiPrimary(checked);
                            if (!checked && primaryModels.length > 1) {
                              setPrimaryModels([primaryModels[0]]);
                            }
                            setHasChanges(true);
                          }}
                          data-testid="multi-primary-switch"
                        />
                        <Label htmlFor="multi-primary" className="text-xs text-[#64748B]">
                          Multi-Primary
                        </Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <ModelSelector
                        label="Primary Model(s)"
                        models={availableModels}
                        selected={primaryModels}
                        onSelect={handlePrimaryModelChange}
                        multiple={allowMultiPrimary}
                        type="primary"
                      />
                      <ModelSelector
                        label="Secondary Model"
                        models={availableModels}
                        selected={secondaryModel ? [secondaryModel] : []}
                        onSelect={handleSecondaryModelChange}
                        multiple={false}
                        type="secondary"
                      />
                    </div>
                  </div>

                  {/* Variables & Token Count */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <Label className="text-xs text-[#94A3B8] flex items-center gap-2">
                        <Variable className="w-3 h-3" />
                        Input Variables ({variables.length})
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {variables.length === 0 ? (
                          <span className="text-xs text-[#64748B]">No variables detected</span>
                        ) : (
                          variables.map(v => (
                            <Badge 
                              key={v} 
                              variant="outline" 
                              className="font-mono text-[11px] bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/30 cursor-pointer hover:bg-[#0EA5E9]/20"
                              onClick={() => insertVariable(v)}
                              title="Click to insert"
                            >
                              {`{${v}}`}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Label className="text-xs text-[#94A3B8] flex items-center gap-1.5 justify-end">
                        <Hash className="w-3 h-3" />
                        Tokens
                      </Label>
                      <div className="text-lg font-mono text-[#0EA5E9] mt-1">
                        {tokenCount.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-[#64748B]">
                        {content.length.toLocaleString()} chars
                      </div>
                      <div className="text-[10px] text-[#64748B]">
                        {content.split('\n').length} lines
                      </div>
                    </div>
                  </div>

                  {/* Prompt Content */}
                  <div className="space-y-2">
                    <Label className="text-xs text-[#94A3B8]">Prompt Content</Label>
                    <textarea
                      ref={textareaRef}
                      value={content}
                      onChange={handleContentChange}
                      className={cn(
                        "w-full font-mono text-sm bg-[#030712] border border-[#1E293B] rounded-sm text-[#F1F5F9] resize-none focus:border-[#334155] focus:ring-1 focus:ring-[#0EA5E9]/30 focus:outline-none p-4 leading-relaxed",
                        isFullscreen ? "min-h-[60vh]" : "min-h-[400px]"
                      )}
                      placeholder="Enter your prompt here... Use {variable} syntax for variables."
                      data-testid="prompt-content-textarea"
                    />
                  </div>

                  {/* Change Note */}
                  {hasChanges && (
                    <div className="space-y-2">
                      <Label className="text-xs text-[#94A3B8]">Change Note (optional)</Label>
                      <input
                        type="text"
                        value={changeNote}
                        onChange={(e) => setChangeNote(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-[#030712] border border-[#1E293B] rounded-sm text-[#F1F5F9] focus:border-[#334155] focus:outline-none"
                        placeholder="Describe your changes..."
                        data-testid="change-note-input"
                      />
                    </div>
                  )}

                  {/* Quick Test Panel */}
                  <div className="space-y-2 border border-[#1E293B] rounded-md overflow-hidden">
                    <button
                      onClick={() => setShowQuickTest(!showQuickTest)}
                      className="w-full flex items-center justify-between p-3 bg-[#0F172A] hover:bg-[#1E293B]/50 transition-colors"
                      data-testid="toggle-quick-test"
                    >
                      <div className="flex items-center gap-2">
                        <Beaker className="w-4 h-4 text-[#0EA5E9]" />
                        <span className="text-sm font-medium text-[#F1F5F9]">Quick Test</span>
                        {hasChanges && (
                          <Badge className="text-[9px] bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30">
                            Using unsaved content
                          </Badge>
                        )}
                        {testCases.length > 0 && (
                          <Badge variant="outline" className="text-[10px]">
                            {testCases.length} test{testCases.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      {showQuickTest ? (
                        <ChevronUp className="w-4 h-4 text-[#64748B]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#64748B]" />
                      )}
                    </button>

                    {showQuickTest && (
                      <div className="p-4 space-y-4 border-t border-[#1E293B] bg-[#030712]/50">
                        {testCases.length === 0 ? (
                          <div className="text-center py-4">
                            <FlaskConical className="w-8 h-8 mx-auto text-[#64748B] mb-2" />
                            <p className="text-sm text-[#64748B]">No test cases yet</p>
                            <p className="text-xs text-[#64748B]">Create test cases in the Test Cases tab</p>
                          </div>
                        ) : (
                          <>
                            {/* Model Selection & Run Button */}
                            <div className="flex items-end gap-3">
                              <div className="flex-1 space-y-1.5">
                                <Label className="text-[10px] text-[#64748B]">Test Model</Label>
                                <div className="flex gap-2">
                                  <Select
                                    value={quickTestModel.provider}
                                    onValueChange={(v) => setQuickTestModel(prev => ({ ...prev, provider: v }))}
                                  >
                                    <SelectTrigger className="w-28 h-8 text-xs bg-[#0F172A] border-[#1E293B]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                                      {Object.keys(availableModels).map(provider => (
                                        <SelectItem key={provider} value={provider} className="text-xs">
                                          {provider}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Select
                                    value={quickTestModel.model}
                                    onValueChange={(v) => setQuickTestModel(prev => ({ ...prev, model: v }))}
                                  >
                                    <SelectTrigger className="flex-1 h-8 text-xs bg-[#0F172A] border-[#1E293B]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                                      {(availableModels[quickTestModel.provider] || []).map(m => (
                                        <SelectItem key={m.name} value={m.name} className="text-xs">
                                          {m.display}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={runSelectedTests}
                                disabled={selectedTestCases.length === 0 || Object.values(runningTests).some(v => v)}
                                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white h-8"
                                data-testid="run-selected-tests"
                              >
                                {Object.values(runningTests).some(v => v) ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Play className="w-4 h-4 mr-1" />
                                )}
                                Run Selected ({selectedTestCases.length})
                              </Button>
                            </div>

                            {/* Select All */}
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="select-all-tests"
                                checked={selectedTestCases.length === testCases.length && testCases.length > 0}
                                onCheckedChange={selectAllTestCases}
                                className="border-[#1E293B] data-[state=checked]:bg-[#0EA5E9] data-[state=checked]:border-[#0EA5E9]"
                              />
                              <Label htmlFor="select-all-tests" className="text-xs text-[#94A3B8] cursor-pointer">
                                Select All
                              </Label>
                            </div>

                            {/* Test Cases List */}
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                              {testCases.map(tc => (
                                <div 
                                  key={tc.id}
                                  className={cn(
                                    "p-3 rounded-md border transition-colors",
                                    selectedTestCases.includes(tc.id)
                                      ? "border-[#0EA5E9]/50 bg-[#0EA5E9]/5"
                                      : "border-[#1E293B] bg-[#0F172A]"
                                  )}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        checked={selectedTestCases.includes(tc.id)}
                                        onCheckedChange={() => toggleTestCaseSelection(tc.id)}
                                        className="border-[#1E293B] data-[state=checked]:bg-[#0EA5E9] data-[state=checked]:border-[#0EA5E9]"
                                        data-testid={`select-test-${tc.id}`}
                                      />
                                      <span className="text-sm text-[#F1F5F9]">{tc.name}</span>
                                      <Badge variant="outline" className="text-[9px]">
                                        {tc.inputs?.length || 0} inputs
                                      </Badge>
                                      {tc.expected_output && (
                                        <Badge variant="outline" className="text-[9px] text-[#0EA5E9] border-[#0EA5E9]/30">
                                          Has Expected
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => runSingleTest(tc.id)}
                                      disabled={runningTests[tc.id]}
                                      className="h-7 px-2"
                                      data-testid={`run-single-test-${tc.id}`}
                                    >
                                      {runningTests[tc.id] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Play className="w-3 h-3" />
                                      )}
                                    </Button>
                                  </div>

                                  {/* Test Result */}
                                  {testResults[tc.id] && (
                                    <div className="mt-2 pt-2 border-t border-[#1E293B]">
                                      <div className="flex items-center gap-2 mb-2">
                                        {testResults[tc.id].success ? (
                                          testResults[tc.id].all_passed ? (
                                            <Badge className="text-[9px] bg-green-500/10 text-green-400 border-green-500/30">
                                              <Check className="w-3 h-3 mr-1" />
                                              Passed
                                            </Badge>
                                          ) : (
                                            <Badge className="text-[9px] bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                                              <AlertCircle className="w-3 h-3 mr-1" />
                                              Some Failed
                                            </Badge>
                                          )
                                        ) : (
                                          <Badge className="text-[9px] bg-red-500/10 text-red-400 border-red-500/30">
                                            <X className="w-3 h-3 mr-1" />
                                            Error
                                          </Badge>
                                        )}
                                        <span className="text-[10px] text-[#64748B]">
                                          {testResults[tc.id].execution_time_ms}ms
                                        </span>
                                        {testResults[tc.id].evaluation_result && (
                                          <Badge 
                                            variant="outline" 
                                            className={cn(
                                              "text-[9px]",
                                              testResults[tc.id].evaluation_result.passed
                                                ? "text-green-400 border-green-500/30"
                                                : "text-red-400 border-red-500/30"
                                            )}
                                          >
                                            {testResults[tc.id].evaluation_result.method}: {
                                              testResults[tc.id].evaluation_result.similarity !== null
                                                ? `${Math.round(testResults[tc.id].evaluation_result.similarity * 100)}%`
                                                : testResults[tc.id].evaluation_result.passed ? 'Match' : 'No Match'
                                            }
                                          </Badge>
                                        )}
                                      </div>
                                      {testResults[tc.id].output && (
                                        <pre className="text-[10px] text-[#94A3B8] bg-[#030712] p-2 rounded border border-[#1E293B] max-h-24 overflow-auto font-mono whitespace-pre-wrap">
                                          {testResults[tc.id].output.substring(0, 500)}
                                          {testResults[tc.id].output.length > 500 && '...'}
                                        </pre>
                                      )}
                                      {testResults[tc.id].error && (
                                        <div className="text-[10px] text-red-400 bg-red-500/10 p-2 rounded">
                                          {testResults[tc.id].error}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="preview" className="flex-1 m-0 h-full overflow-auto">
              <PromptPreview 
                content={content} 
                variables={variables}
                highlightedContent={highlightedContent}
              />
            </TabsContent>

            <TabsContent value="test" className="flex-1 m-0 h-full overflow-auto">
              <TestCasePanel 
                prompt={prompt}
                content={content}
                variables={variables}
                availableModels={availableModels}
                useCurrentContent={hasChanges}
                onImportComplete={onImportComplete}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Version History Panel */}
        {showHistory && (
          <VersionHistory
            versions={versions}
            currentVersion={prompt.current_version}
            promptId={prompt.id}
            onRestore={handleRestore}
            onClose={() => setShowHistory(false)}
            onVersionDeleted={handleVersionDeleted}
          />
        )}
      </div>
    </div>
  );
}
